import { IonApp } from './components/ionic';
import { normalizeRoomCode, routeForAppLink, type AppLinkRoute } from '@calling/shared';
import { createElement, lazy, Suspense, useEffect, useRef, useState, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Dialog, Loading, Toasts, useToasts } from './components/ui';
import { UpdateGate, useUpdateNeeded } from './components/UpdateGate';
import { WelcomeDialog } from './components/Welcome';
import { activeCall } from './lib/activeCall';
import { api, ApiError, takeNativeVerifier } from './lib/api';
import { signInResults, type SignInResult } from './lib/appleSignIn';
import { handleBackButton } from './lib/backButton';
import { APP_NAME, isNative, platform } from './lib/platform';
import { useSession } from './lib/useStore';
import Home from './pages/Home';
import Meeting from './pages/Meeting';
import SignIn, { useServerInfo } from './pages/SignIn';

/**
 * A page whose code loads when it is first opened. Once loaded it draws at
 * once, without a "Loading" screen in between; `preload` loads it before the
 * app first draws (see preloadPage).
 */
function preloadable<P extends object>(load: () => Promise<ComponentType<P>>): ComponentType<P> & { preload: () => Promise<unknown> } {
  let loaded: ComponentType<P> | null = null;
  const preload = () => load().then((page) => (loaded = page));
  const Lazy = lazy(() => preload().then((page) => ({ default: page })));
  const Page = (props: P) => createElement(loaded ?? Lazy, props);
  return Object.assign(Page, { preload });
}

const Landing = lazy(() => import('./pages/Landing'));
const Billing = lazy(() => import('./pages/Billing'));
const Settings = lazy(() => import('./pages/Settings'));
const Recordings = lazy(() => import('./pages/Recordings'));
const Integrations = lazy(() => import('./pages/Integrations'));
const Shared = lazy(() => import('./pages/Shared'));
const Watch = lazy(() => import('./pages/Watch'));
const Help = lazy(() => import('./pages/Info').then((m) => ({ default: m.Help })));
const Privacy = preloadable(() => import('./pages/Info').then((m) => m.Privacy));
const Terms = preloadable(() => import('./pages/Info').then((m) => m.Terms));
const AccountDeletion = preloadable(() => import('./pages/AccountDeletion').then((m) => m.default));
const ConfirmDeletion = lazy(() => import('./pages/AccountDeletion').then((m) => ({ default: m.ConfirmDeletion })));
const VerifyEmail = lazy(() => import('./pages/EmailLink').then((m) => ({ default: m.VerifyEmail })));
const ResetPassword = lazy(() => import('./pages/EmailLink').then((m) => ({ default: m.ResetPassword })));
const DevMailbox = lazy(() => import('./pages/DevMailbox'));
const Reports = lazy(() => import('./pages/Reports'));

/**
 * The server writes the words of these pages into their HTML, for readers
 * without JavaScript. Their code is loaded before the app first draws
 * (main.tsx), so the app's copy replaces the server's directly.
 */
export function preloadPage(pathname: string): Promise<unknown> {
  if (pathname === '/privacy') return Privacy.preload();
  if (pathname === '/terms') return Terms.preload();
  if (pathname === '/delete-account' && !isNative) return AccountDeletion.preload();
  return Promise.resolve();
}

const PROVIDER_ADDED = 'A new sign-in method was added to your account.';

/** Reads the outcome the server appended to the URL after sign-in, then removes it from the address bar. */
function useSignInOutcome(): { outcome: string | null; error: string | null; clear: () => void } {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState<{ outcome: string | null; error: string | null }>({ outcome: null, error: null });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get('signed_in');
    const error = params.get('auth_error');
    if (!outcome && !error) return;
    setResult({ outcome, error });
    params.delete('signed_in');
    params.delete('auth_error');
    const rest = params.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);
  return { ...result, clear: () => setResult({ outcome: null, error: null }) };
}

/** A link that would take the app out of the call it is in, waiting for the person to say whether to follow it. */
interface LinkDuringCall {
  route: Exclude<AppLinkRoute, { kind: 'invalid' }>;
  title: string;
}

/**
 * Native apps: sign-in finishes when the system browser hands back a one-time
 * code; meeting links, and the pages of recordings shared by email, open the
 * app (E2-S6). A meeting link keeps the organiser's host token (?host=), so a
 * host link from another product makes its organiser the host in the app as
 * on the website. A link opened during a call asks first (`duringCall`).
 */
function useDeepLinks(onOutcome: (outcome: string | null, error: string | null) => void, show: (text: string, tone?: 'info' | 'warning' | 'danger') => void): {
  duringCall: LinkDuringCall | null;
  stay: () => void;
  follow: () => void;
} {
  const navigate = useNavigate();
  const [duringCall, setDuringCall] = useState<LinkDuringCall | null>(null);
  // The latest of each, so that the listener is added only once: two listeners would each take the same link.
  const latest = useRef({ onOutcome, show, navigate });
  latest.current = { onOutcome, show, navigate };
  useEffect(() => {
    if (!isNative) return;
    let removed = false;
    let remove: (() => void) | undefined;
    const onOutcome = (outcome: string | null, error: string | null) => latest.current.onOutcome(outcome, error);
    const show = (text: string, tone?: 'info' | 'warning' | 'danger') => latest.current.show(text, tone);
    const navigate = (path: string, options?: { replace?: boolean }) => latest.current.navigate(path, options);
    void (async () => {
      const { App } = await import('@capacitor/app');
      const handle = await App.addListener('appUrlOpen', async ({ url }) => {
        if (/^https?:/i.test(url)) {
          const route = routeForAppLink(url);
          if (!route) return;
          if (route.kind === 'invalid') {
            show('That link isn’t a meeting link. Check it, or ask for a new one.', 'warning');
            navigate('/');
            return;
          }
          const call = activeCall.get();
          // The meeting the person is already in: nothing to open.
          if (call && route.kind === 'meeting' && route.code === call.code) return;
          if (call) return setDuringCall({ route, title: call.title });
          navigate(route.path);
          return;
        }
        // The sign-in return: <scheme>://auth?code=…
        const parsed = new URL(url);
        const { Browser } = await import('@capacitor/browser');
        await Browser.close().catch(() => {});
        // <scheme>://auth?back=1: a page in the browser (a sign-up confirmed, a password chosen) sends the person back. The app signs in by itself.
        if (parsed.searchParams.get('back') === '1') return;
        const error = parsed.searchParams.get('error');
        const code = parsed.searchParams.get('code');
        const verifier = takeNativeVerifier();
        if (error || !code || !verifier) return onOutcome(null, error ?? 'code_invalid');
        try {
          await api.exchangeNativeCode(code, verifier);
          onOutcome(parsed.searchParams.get('signed_in'), null);
          navigate(parsed.searchParams.get('return_to') || '/', { replace: true });
        } catch (err) {
          onOutcome(null, err instanceof ApiError && err.code === 'account_suspended' ? 'account_suspended' : 'code_invalid');
        }
      });
      remove = () => void handle.remove();
      // Unmounted while the listener was being added.
      if (removed) remove();
    })();
    return () => {
      removed = true;
      remove?.();
    };
  }, []);
  return {
    duringCall,
    stay: () => setDuringCall(null),
    follow: () => {
      // Leaving the meeting's page leaves the call and frees the camera (Meeting.tsx).
      if (duringCall) navigate(duringCall.route.path);
      setDuringCall(null);
    },
  };
}

/**
 * Android's back button and back gesture (lib/backButton.ts): they close a
 * dialog, ask before leaving a call, go back a page, or put the app in the
 * background from the first page. Without a listener the web view only went
 * back a page, leaving a call without asking.
 */
function useAndroidBackButton(): void {
  useEffect(() => {
    if (platform !== 'android') return;
    let removed = false;
    let remove: (() => void) | undefined;
    void (async () => {
      const { App } = await import('@capacitor/app');
      const handle = await App.addListener('backButton', ({ canGoBack }) => {
        handleBackButton(canGoBack, { back: () => window.history.back(), background: () => void App.minimizeApp() });
      });
      remove = () => void handle.remove();
      if (removed) remove();
    })();
    return () => {
      removed = true;
      remove?.();
    };
  }, []);
}

/**
 * A meeting's page, made anew for each meeting, and for each new host link:
 * the host token, and everything else a meeting page remembers, belong to one
 * meeting. A link opened in the apps can lead from one meeting's page to
 * another's, which would otherwise keep the first one's token. (The page
 * takes the token out of the address once it has it; that changes nothing.)
 */
function MeetingRoute() {
  const { code = '' } = useParams();
  const host = new URLSearchParams(useLocation().search).get('host');
  const meeting = normalizeRoomCode(code) ?? code;
  const [opened, setOpened] = useState({ meeting, host: host ?? '', count: 0 });
  if (opened.meeting !== meeting || (host && host !== opened.host)) setOpened({ meeting, host: host ?? '', count: opened.count + 1 });
  return <Meeting key={opened.count} />;
}

function Screens() {
  const session = useSession();
  const { outcome, error, clear } = useSignInOutcome();
  const [native, setNative] = useState<{ outcome: string | null; error: string | null }>({ outcome: null, error: null });
  const { toasts, show, dismiss } = useToasts(6000);
  const [welcome, setWelcome] = useState(false);
  // Only say "we emailed you" when the server really sends email to this address.
  const server = useServerInfo();
  const serverKnown = server.info !== null || server.failed;
  const emails = (server.info?.emailEnabled ?? false) && (session.user?.emailVerified ?? false);
  const links = useDeepLinks((o, e) => setNative({ outcome: o, error: e }), show);
  // The iPhone's Sign in with Apple sheet never leaves the app: it tells how it went here, as a return address would.
  useEffect(() => {
    const told = (event: Event) => {
      const { outcome, error } = (event as CustomEvent<SignInResult>).detail;
      setNative({ outcome: outcome ?? null, error: error ?? null });
    };
    signInResults.addEventListener('result', told);
    return () => signInResults.removeEventListener('result', told);
  }, []);
  const update = useUpdateNeeded();
  useAndroidBackButton();

  const signedInAs = outcome ?? native.outcome;
  // A device signed out because the account was suspended says so on the sign-in screen.
  const authError = error ?? native.error ?? (session.status === 'signedOut' ? (session.endedBy ?? null) : null);
  useEffect(() => {
    if (session.status !== 'signedIn' || !signedInAs || !serverKnown) return;
    if (signedInAs === 'created') setWelcome(true);
    else if (signedInAs === 'linked') show(emails ? `${PROVIDER_ADDED} We emailed you a confirmation.` : PROVIDER_ADDED);
    clear();
    setNative({ outcome: null, error: null });
    // Runs when the outcome arrives; `show` and `clear` are stable enough for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.status, signedInAs, serverKnown]);

  const dismissError = () => {
    clear();
    setNative({ outcome: null, error: null });
    api.dismissEnded();
  };
  const user = session.user;
  const signIn = <SignIn authError={authError} onDismissError={dismissError} />;
  const gate = (page: (u: NonNullable<typeof user>) => React.ReactNode) => (session.status === 'loading' ? <Loading /> : user ? page(user) : signIn);
  // Visitors on the web are shown what the product is; the apps open at sign-in.
  const front = isNative ? signIn : <Landing authError={authError} onDismissError={dismissError} />;

  // An app older than the server still works with can only be updated.
  if (update.needed) return <UpdateGate appId={update.appId} />;
  return (
    <>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={session.status === 'loading' ? <Loading /> : user ? <Home user={user} /> : front} />
          <Route path="/pricing" element={session.status === 'loading' ? <Loading /> : user ? <Navigate to="/billing" replace /> : isNative ? <Navigate to="/" replace /> : front} />
          <Route path="/billing" element={gate((u) => <Billing user={u} />)} />
          <Route path="/m/:code" element={<MeetingRoute />} />
          <Route path="/recordings" element={gate((u) => <Recordings user={u} />)} />
          {/* Signed out, it asks for sign-in itself, saying why: it is what an email about a shared recording opens. */}
          <Route path="/recordings/:id" element={<Watch authError={authError} onDismissError={dismissError} />} />
          <Route path="/settings" element={gate((u) => <Settings user={u} />)} />
          <Route path="/integrations" element={gate((u) => <Integrations user={u} />)} />
          {/* What people reported, for super admins (Settings > Super admin > Open reports). */}
          <Route path="/reports" element={gate((u) => <Reports user={u} />)} />
          <Route path="/reports/:id" element={gate((u) => <Reports user={u} />)} />
          <Route path="/r/:token" element={<Shared />} />
          <Route path="/help" element={<Help />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          {/* The deletion pages are the website's; in the apps an account is deleted in Settings. */}
          <Route path="/delete-account" element={isNative ? <Navigate to="/settings?delete=1" replace /> : <AccountDeletion />} />
          <Route path="/delete-account/confirm" element={isNative ? <Navigate to="/" replace /> : <ConfirmDeletion />} />
          {/* The pages the links in our emails open: on the website, never in the apps. */}
          <Route path="/verify-email" element={isNative ? <Navigate to="/" replace /> : <VerifyEmail />} />
          <Route path="/reset-password" element={isNative ? <Navigate to="/" replace /> : <ResetPassword />} />
          {/* A developer's own server without SMTP shows the emails it would have sent here (server/src/dev/mailbox.ts). */}
          <Route path="/dev/mailbox" element={isNative ? <Navigate to="/" replace /> : <DevMailbox />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      {welcome && user ? <WelcomeDialog user={user} passwordOffered={server.info?.password === true} emails={emails} onClose={() => setWelcome(false)} /> : null}
      {links.duringCall ? (
        <Dialog title={links.duringCall.route.kind === 'meeting' ? 'Open another meeting?' : 'Open this recording?'} onClose={links.stay} testId="link-during-call">
          <p>You’re in “{links.duringCall.title}”. Opening the link leaves it.</p>
          <div className="dialog__actions">
            <button className="btn btn--ghost" onClick={links.stay}>
              Stay here
            </button>
            <button className="btn btn--primary" onClick={links.follow}>
              Leave and open
            </button>
          </div>
        </Dialog>
      ) : null}
      <Toasts items={toasts} onDismiss={dismiss} />
    </>
  );
}

export default function App() {
  useEffect(() => {
    void api.init();
  }, []);
  return (
    <IonApp>
      {/* The two behaviours React Router makes standard in v7, adopted now. */}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* A page that crashes says so and offers a reload, rather than leaving a blank screen. */}
        <ErrorBoundary>
          <Screens />
        </ErrorBoundary>
      </BrowserRouter>
    </IonApp>
  );
}
